import { inject } from '@angular/core';
import type { CanActivateFn, UrlTree } from '@angular/router';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import type { AuthPermission } from '../api/generated/models/AuthPermission';
import type { AuthRole } from '../api/generated/models/AuthRole';
import { createLoginRedirect } from './auth.guard';
import { AuthStore } from './auth.store';

export type RbacRouteData = {
  roles?: readonly AuthRole[];
  permissions?: readonly AuthPermission[];
};

export const rbacGuard: CanActivateFn = (route, state) => {
  const authStore = inject(AuthStore);
  const router = inject(Router);
  const data = route.data as RbacRouteData;

  return authStore.ensureSession().pipe(
    map((isAuthenticated) => {
      if (!isAuthenticated) {
        return createLoginRedirect(router, state);
      }

      return hasRouteAccess(authStore, data) ? true : router.parseUrl('/forbidden');
    }),
  );
};

export function roleGuard(roles: readonly AuthRole[]): CanActivateFn {
  return (_route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    return authStore.ensureSession().pipe(
      map((isAuthenticated): boolean | UrlTree => {
        if (!isAuthenticated) {
          return createLoginRedirect(router, state);
        }

        return authStore.hasAnyRole(roles) ? true : router.parseUrl('/forbidden');
      }),
    );
  };
}

export function permissionGuard(permissions: readonly AuthPermission[]): CanActivateFn {
  return (_route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    return authStore.ensureSession().pipe(
      map((isAuthenticated): boolean | UrlTree => {
        if (!isAuthenticated) {
          return createLoginRedirect(router, state);
        }

        return authStore.hasEveryPermission(permissions) ? true : router.parseUrl('/forbidden');
      }),
    );
  };
}

export function anyPermissionGuard(permissions: readonly AuthPermission[]): CanActivateFn {
  return (_route, state) => {
    const authStore = inject(AuthStore);
    const router = inject(Router);

    return authStore.ensureSession().pipe(
      map((isAuthenticated): boolean | UrlTree => {
        if (!isAuthenticated) {
          return createLoginRedirect(router, state);
        }

        return authStore.hasAnyPermission(permissions) ? true : router.parseUrl('/forbidden');
      }),
    );
  };
}

function hasRouteAccess(authStore: AuthStore, data: RbacRouteData) {
  const roles = data.roles ?? [];
  const permissions = data.permissions ?? [];

  return authStore.hasAnyRole(roles) && authStore.hasEveryPermission(permissions);
}
